import React, { useCallback, useEffect } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet, ActivityIndicator, LayoutAnimation, TouchableOpacity, Text } from 'react-native';
import { LinkedAccountsSkeleton } from '@/screens/residents/linkedAccounts/LinkedAccountsSkeleton';
import { useAppDispatch, useAppSelector } from '@/store';
import { UserType } from '@/types/residents/user';
import { LinkedAccount, Invitation } from '@/types/residents/home';
import { fetchInvitations, acceptInvitation, resendInvitation } from '@/store/slices/residents/invitationsSlice';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { useToast } from '@/contexts/admin/ToastContext';
import { getDisplayName } from '@/utils/residents/utils';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PageHeader } from '../PageHeader';
import { SectionHeader } from '../SectionHeader';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { LinkedAccountsTabSegment, LinkedAccountsTab } from './LinkedAccountsTabSegment';
import { LinkedAccountCard } from './LinkedAccountCard';
import { InvitationCard } from './InvitationCard';
import { MobileEmptyState } from '@/components/residents/linkedAccounts/mobile/MobileEmptyState';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';

interface LinkedAccountsContentProps {
  selectedTab: LinkedAccountsTab;
  onTabChange: (tab: LinkedAccountsTab) => void;
  accounts: LinkedAccount[];
  isLoading: boolean;
  onRefresh: () => void;
  onRemove: (account: LinkedAccount) => void;
  onEmailPress?: (email: string) => void;
  onSendInvite: () => void;
  onDeclineInvitation: (invitation: Invitation) => void;
  onCancelInvitation: (invitation: Invitation) => void;
  contentPaddingBottom?: number;
}

export const LinkedAccountsContent: React.FC<LinkedAccountsContentProps> = React.memo(({
  selectedTab,
  onTabChange,
  accounts,
  isLoading,
  onRefresh,
  onRemove,
  onEmailPress,
  onSendInvite,
  onDeclineInvitation,
  onCancelInvitation,
  contentPaddingBottom = 40,
}) => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const { invitations, isLoading: isLoadingInvitations } = useAppSelector((s) => s.invitations);
  const { user } = useAppSelector((s) => s.auth);
  const isResident = user?.type === UserType.RESIDENT;
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    dispatch(fetchInvitations());
  }, [dispatch]);

  useEffect(() => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
  }, [selectedTab]);

  const handleAccept = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(acceptInvitation(invitation.id)).unwrap();
      const displayName = getDisplayName(invitation.user.firstName, invitation.user.lastName) || invitation.user.email;
      showSuccess(`You have successfully accepted an invitation from ${displayName}`);
      dispatch(fetchLinkedAccounts());
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to accept invitation');
    }
  }, [dispatch, showSuccess, showError]);

  const handleResend = useCallback(async (invitation: Invitation) => {
    try {
      await dispatch(resendInvitation(invitation.id)).unwrap();
      showSuccess(`Invitation resent to ${invitation.user.email}`);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Failed to resend invitation');
    }
  }, [dispatch, showSuccess, showError]);

  const receivedInvitations = invitations.filter((inv) => !inv.sender);
  const pendingInvitations = invitations;

  const renderAccountsTab = () => (
    <>
      {/* Connected Section */}
      <SectionHeader title="Connected" />

      {isLoading ? (
        <LinkedAccountsSkeleton />
      ) : accounts.length === 0 ? (
        <MobileEmptyState
          icon={<RESIDENTS_SVG_COMPONENTS.NO_CONNECTION width={60} height={60} color={colors.text.dimmer} />}
          title="No connected accounts"
          description="You don't have any linked accounts yet."
          iconWidth={60}
          iconHeight={60}
        />
      ) : (
        <View style={styles.cardList}>
          {accounts.map((account) => (
            <LinkedAccountCard
              key={account.id}
              account={account}
              onRemove={onRemove}
              onEmailPress={onEmailPress}
            />
          ))}
        </View>
      )}

      {/* Pending Invitations Preview (on Accounts tab) */}
      {receivedInvitations.length > 0 && (
        <>
          <SectionHeader
            title="Pending"
            actionLabel="View all"
            onAction={() => onTabChange('invitations')}
          />
          <View style={styles.cardList}>
            {receivedInvitations.slice(0, 2).map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                onAccept={handleAccept}
                onDecline={onDeclineInvitation}
              />
            ))}
          </View>
        </>
      )}

    </>
  );

  const renderInvitationsTab = () => (
    <>
      <SectionHeader title="All Invitations" />

      {isLoadingInvitations ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator color={colors.text.muted} />
        </View>
      ) : pendingInvitations.length === 0 ? (
        <MobileEmptyState
          icon={<RESIDENTS_SVG_COMPONENTS.EMPTY_INBOX width={60} height={60} color={colors.text.muted} />}
          title="No invitations"
          description="You don't have any pending invitations."
          iconWidth={60}
          iconHeight={60}
        />
      ) : (
        <>
          <View style={styles.cardList}>
            {pendingInvitations.map((invitation) => (
              <InvitationCard
                key={invitation.id}
                invitation={invitation}
                onAccept={!invitation.sender ? handleAccept : undefined}
                onDecline={!invitation.sender ? onDeclineInvitation : undefined}
                onCancel={invitation.sender ? onCancelInvitation : undefined}
                onResend={invitation.sender ? handleResend : undefined}
              />
            ))}
          </View>
        </>
      )}
    </>
  );

  return (
    <ScrollView
      style={styles.scrollView}
      contentContainerStyle={[styles.scrollContent, { paddingBottom: contentPaddingBottom }]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={isLoading || isLoadingInvitations}
          onRefresh={() => {
            onRefresh();
            dispatch(fetchInvitations());
          }}
          tintColor={colors.text.primary}
        />
      }
    >
      <ScreenBackLink style={styles.backLink} />

      <PageHeader
        title="Linked Accounts"
        subtitle="Manage connections & invitations"
        rightAction={isResident ? (
          <TouchableOpacity
            style={styles.headerInviteButton}
            onPress={onSendInvite}
            activeOpacity={0.7}
          >
            <Text style={styles.headerInviteButtonText}>+</Text>
          </TouchableOpacity>
        ) : undefined}
      />

      <LinkedAccountsTabSegment
        selectedTab={selectedTab}
        accountsCount={accounts.length}
        invitationsCount={invitations.length}
        onTabChange={onTabChange}
      />

      {selectedTab === 'accounts' ? renderAccountsTab() : renderInvitationsTab()}
    </ScrollView>
  );
});

LinkedAccountsContent.displayName = 'LinkedAccountsContent';

const HP = REDESIGN_SPACING.horizontalPadding;

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
  },
  backLink: {
    marginBottom: 4,
  },
  cardList: {
    gap: 12,
  },
  loadingContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  headerInviteButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInviteButtonText: {
    fontSize: 22,
    fontWeight: '600',
    color: PRIMITIVE_COLORS.white,
    lineHeight: 24,
  },
});
